import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/primitives";
import { CONTACT } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Name", type: "text", placeholder: "Your full name" },
  { name: "brand", label: "Brand Name", type: "text", placeholder: "Your brand or label" },
  { name: "instagram", label: "Instagram Link", type: "text", placeholder: "instagram.com/yourbrand" },
  { name: "email", label: "Email", type: "email", placeholder: "you@brand.com" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", brand: "", instagram: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.brand || !form.instagram || !form.email || !form.message) {
      toast.error("Please complete every field.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. We'll be in touch shortly.");
      setForm({ name: "", brand: "", instagram: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-ivory pt-36 md:pt-48 pb-28 md:pb-40" data-testid="contact-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        <Reveal><Eyebrow>Get In Touch</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-6 font-serif font-light text-charcoal leading-[0.98] tracking-tight text-6xl md:text-8xl lg:text-[7vw] max-w-4xl">
            Let&rsquo;s grow together.
          </h1>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-16">
          {/* Form */}
          <div className="md:col-span-7">
            <Reveal>
              <form onSubmit={handleSubmit} className="space-y-10" data-testid="contact-form">
                {FIELDS.map((f) => (
                  <div key={f.name} className="relative">
                    <label className="block font-sans text-xs uppercase tracking-[0.2em] text-gold mb-3">
                      {f.label}
                    </label>
                    <input
                      type={f.type}
                      name={f.name}
                      value={form[f.name]}
                      onChange={handleChange}
                      placeholder={f.placeholder}
                      data-testid={`contact-${f.name}`}
                      className="w-full bg-transparent border-b border-charcoal/25 focus:border-gold outline-none py-3 font-sans text-lg text-charcoal placeholder:text-charcoal/30 transition-colors duration-300"
                    />
                  </div>
                ))}
                <div>
                  <label className="block font-sans text-xs uppercase tracking-[0.2em] text-gold mb-3">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your brand and where you want to go."
                    data-testid="contact-message"
                    className="w-full bg-transparent border-b border-charcoal/25 focus:border-gold outline-none py-3 font-sans text-lg text-charcoal placeholder:text-charcoal/30 transition-colors duration-300 resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="group inline-flex items-center gap-3 px-8 py-4 rounded-sm bg-charcoal text-ivory font-sans text-sm tracking-wide hover:bg-gold transition-colors duration-300 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Book a Strategy Call"}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                </button>
              </form>
            </Reveal>
          </div>

          {/* Details */}
          <div className="md:col-span-4 md:col-start-9">
            <Reveal delay={0.1}>
              <div className="space-y-10">
                <div>
                  <p className="font-sans text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Email</p>
                  <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="font-serif text-lg md:text-xl text-charcoal hover:text-gold transition-colors duration-300 break-words">
                    {CONTACT.email}
                  </a>
                </div>
                <div>
                  <p className="font-sans text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">WhatsApp</p>
                  <a href="https://wa.me/919953080505" target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="font-serif text-2xl text-charcoal hover:text-gold transition-colors duration-300">
                    {CONTACT.whatsapp}
                  </a>
                </div>
                <div>
                  <p className="font-sans text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Studios</p>
                  <p className="font-serif text-2xl text-charcoal">{CONTACT.cities.join(" · ")}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
